import { useEffect, useRef, useState, useCallback } from "react";
import { API_BASE } from "@/lib/api";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Voice-to-text button using MediaRecorder + Whisper.
 * Tap to start, tap to stop → transcribes → calls onTranscript(text).
 * Shows red pulse + live timer while recording.
 */
export default function VoiceButton({ onTranscript, disabled = false, language = "en" }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRef.current = null;
    chunksRef.current = [];
    setSeconds(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const upload = async (blob) => {
    setState("transcribing");
    try {
      const fd = new FormData();
      fd.append("file", blob, `voice.${blob.type.includes("webm") ? "webm" : "ogg"}`);
      fd.append("language", language || "en");
      const token = localStorage.getItem("alphacore_token") || "";
      const res = await fetch(`${API_BASE}/voice/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const text = (data.text || "").trim();
      if (!text) {
        toast("No speech detected. Try again a little louder.");
      } else {
        onTranscript?.(text);
      }
    } catch (e) {
      toast.error(e.message || "Transcription failed");
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    if (state !== "idle") return;
    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error("Voice input isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 } });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 96000 });
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        cleanup();
        if (blob.size < 2000) {
          toast("That clip was too short. Hold the button a bit longer.");
          setState("idle");
          return;
        }
        upload(blob);
      };
      rec.start(250);
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      if (e.name === "NotAllowedError") toast.error("Microphone access denied. Enable it in your browser settings.");
      else toast.error(e.message || "Could not start recording");
      cleanup();
      setState("idle");
    }
  };

  const stop = () => {
    if (state !== "recording") return;
    try { mediaRef.current?.stop(); } catch { /* noop */ }
  };

  const busy = state === "transcribing";
  const rec = state === "recording";

  return (
    <button
      type="button"
      onClick={rec ? stop : start}
      disabled={disabled || busy}
      className={`h-12 w-12 rounded-md border flex items-center justify-center transition-all shrink-0 ${
        rec
          ? "border-red-400/50 bg-red-500/10 text-red-300"
          : busy
          ? "border-white/10 text-white/40"
          : "border-white/10 hover:bg-white/5 text-white/70 hover:text-white"
      }`}
      title={rec ? `Stop (${seconds}s)` : busy ? "Transcribing…" : "Speak to AlphaCore AI"}
      data-testid="voice-input-btn"
      aria-label={rec ? "Stop recording" : "Start voice input"}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : rec ? (
        <div className="flex items-center gap-1.5">
          <span className="relative flex items-center justify-center">
            <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-60 animate-ping" />
            <Square className="relative h-3.5 w-3.5 fill-red-400 text-red-400" />
          </span>
          <span className="text-[11px] font-mono" data-testid="voice-timer">{seconds}s</span>
        </div>
      ) : (
        <Mic className="h-4 w-4" />
      )}
    </button>
  );
}
